// Triage state for the feed: what have I already seen, what am I watching, what
// do I not want to hear about.
//
// THE CURSOR ALREADY EXISTS AND IS EXACT. `rev_seq` is the global change counter
// the SPA already polls on, so "unread" is `e.rev > lastReadRev` — no new server
// state, no timestamps to compare, no per-user table. And the merge case falls
// out for free: a corroborating report bumps rev, so a situation that GREW
// re-enters "new" while its AGE (the occurrence clock) stays honest. rev is the
// change signal; occurrence time is the age signal. Before migration 031
// `valid_from` was wrongly doing both, which is why a corroborated event looked
// like breaking news.
//
// Every localStorage key this feature uses lives in THIS FILE, so "what does the
// feed persist" is one grep rather than a hunt through panels.ts.

import type { EntityDto } from './api';
import { normalizePlace } from './situations';

const READ_REV = 'cockpit-feed-readrev';
const PINS = 'cockpit-feed-pins';
const MUTES = 'cockpit-feed-mutes';
const DISMISSED = 'cockpit-feed-dismissed';
const ALERT_DISMISSED = 'cockpit-alert-dismissed';

interface Mutes { places: string[]; channels: string[] }

// EVERY storage touch goes through these two, including the plain-number cursor.
// Reading and writing localStorage throws in more situations than people expect —
// Safari private mode, a full quota, a hardened profile — and triage is a
// convenience: it may degrade, but it must never take the feed down with it. An
// unguarded setItem in a click handler is how a quota error becomes a dead button.
// CACHED IN MEMORY, and that is a performance fact, not a convenience. isPinned /
// isMuted / isDismissed / isUnread are asked once per event per feed repaint —
// inside filters AND sort comparators — and each used to be a synchronous
// localStorage.getItem plus a JSON.parse of the whole list (the dismissed list
// alone can be 2,000 ids). Thousands of parses of the same string per repaint
// was a measurable share of every 15 s freeze. Every write goes through
// writeRaw, so the cache is invalidated at the one place state changes; a
// `storage` event covers another tab, and a swapped-in store (the tests
// install a fresh stub per case) drops the cache by identity.
const rawCache = new Map<string, string | null>();
const setCache = new Map<string, Set<string>>();
let mutesCache: Mutes | null = null;
let readRevCache: number | null = null;
let storageRef: unknown = undefined;

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;   // a hardened profile throws on the property itself
  }
}

function dropDerived(key: string): void {
  setCache.delete(key);
  if (key === MUTES) mutesCache = null;
  if (key === READ_REV) readRevCache = null;
}

function dropAll(): void {
  rawCache.clear();
  setCache.clear();
  mutesCache = null;
  readRevCache = null;
}

/** The store, with the cache dropped if it is not the one the cache was built
 *  from. Every cached accessor calls this first, not just readRaw — a cache hit
 *  that skipped the check would outlive a swapped store. */
function syncStore(): Storage | null {
  const store = storage();
  if (store !== storageRef) {
    storageRef = store;
    dropAll();
  }
  return store;
}

function readRaw(key: string): string | null {
  const store = syncStore();
  if (rawCache.has(key)) return rawCache.get(key)!;
  let value: string | null;
  try {
    value = store ? store.getItem(key) : null;
  } catch {
    value = null;
  }
  rawCache.set(key, value);
  return value;
}

function writeRaw(key: string, value: string): void {
  dropDerived(key);
  try {
    storage()?.setItem(key, value);
    rawCache.set(key, value);
  } catch {
    // Triage degrades, the feed does not — and the cache must not claim a
    // state the store refused, so the next read asks the store again.
    rawCache.delete(key);
  }
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (ev) => {
    if (ev.key === null) dropAll();
    else if (rawCache.has(ev.key)) { rawCache.delete(ev.key); dropDerived(ev.key); }
    else return;
    changed('triage');
  });
}

function readJson<T>(key: string, fallback: T): T {
  const raw = readRaw(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;   // corrupt storage must never break the feed
  }
}

function writeJson(key: string, value: unknown): void {
  writeRaw(key, JSON.stringify(value));
}

/** What moved: 'read' means only the read cursor advanced — rows keep their
 *  order and contents, so a listener can restyle in place instead of
 *  rebuilding the pane. 'triage' (pin, mute, dismiss) changes what is shown. */
export type ReadStateChange = 'read' | 'triage';
type Listener = (kind: ReadStateChange) => void;
let listeners: Listener[] = [];
export function onReadStateChange(cb: Listener): void { listeners.push(cb); }
/** Re-entrant renderApp calls install fresh listeners; drop the stale ones. */
export function resetReadStateListeners(): void { listeners = []; }
function changed(kind: ReadStateChange): void { for (const cb of listeners) cb(kind); }

// --- unread ------------------------------------------------------------------

export function lastReadRev(): number {
  syncStore();
  if (readRevCache === null) readRevCache = Number(readRaw(READ_REV) ?? 0) || 0;
  return readRevCache;
}

/** Machine re-attribution. Neither changes what HAPPENED, so neither is news.
 *
 *  'refile' changes an object's SCOPE — filing 441 damage markers into a story
 *  writes 441 revs (CLAUDE.md's thundering herd), and one curator action would
 *  otherwise mark the entire feed unread for everybody.
 *
 *  'route' changes which INCIDENT a report belongs to (migration 033). The first
 *  routing pass touches every unrouted event at once, so without this the
 *  feature would announce itself by destroying the read state of every reader —
 *  the exact state the analyst relies on to answer "what did I miss". */
const NOT_NEWS = new Set(['refile', 'route']);

/** Has this event changed since the reader last marked the feed read? */
export function isUnread(e: EntityDto): boolean {
  if (NOT_NEWS.has(e.changeKind)) return false;
  return (e.rev ?? 0) > lastReadRev();
}

/** Mark read up to the highest rev the reader was ACTUALLY SHOWN.
 *
 *  Never `dto.rev` from the updates poll: that cursor advances past events this
 *  client never received — a `truncated` poll (CLAUDE.md's merge cap) drops
 *  entities on the floor, and marking up to dto.rev would silently bury them. So
 *  the caller passes what it rendered. */
export function markRead(shown: EntityDto[]): void {
  const max = shown.reduce((m, e) => Math.max(m, e.rev ?? 0), lastReadRev());
  writeRaw(READ_REV, String(max));
  changed('read');
}

// --- pin / mute / dismiss ----------------------------------------------------

/** The parsed id set, cached until the key is written. Callers that mutate it
 *  write it straight back, which drops the cache — so the mutation is never
 *  observed by a reader between the two. */
function ids(key: string): Set<string> {
  syncStore();
  let set = setCache.get(key);
  if (!set) {
    set = new Set(readJson<string[]>(key, []));
    setCache.set(key, set);
  }
  return set;
}

export function isPinned(e: EntityDto): boolean { return ids(PINS).has(e.id); }
export function togglePin(e: EntityDto): void {
  const set = ids(PINS);
  if (!set.delete(e.id)) set.add(e.id);
  writeJson(PINS, [...set]);
  changed('triage');
}

export function isDismissed(e: EntityDto): boolean { return ids(DISMISSED).has(e.id); }
export function dismiss(e: EntityDto): void {
  const set = ids(DISMISSED);
  set.add(e.id);
  // Bounded: dismissals are per-event and the feed is unbounded over time.
  writeJson(DISMISSED, [...set].slice(-2000));
  changed('triage');
}
export function undismissAll(): void { writeJson(DISMISSED, []); changed('triage'); }

/** Alert dismissal is SEPARATE from feed dismissal on purpose: silencing the
 *  map's area flash must not hide the report from the feed — the flash is an
 *  attention device, the row is the record. (Feed-dismissing an event does
 *  also silence its flash: hiding the record and keeping the alarm would be
 *  the worse inconsistency.) */
export function isAlertDismissed(id: string): boolean {
  return ids(ALERT_DISMISSED).has(id);
}
export function dismissAlert(id: string): void {
  const set = ids(ALERT_DISMISSED);
  set.add(id);
  writeJson(ALERT_DISMISSED, [...set].slice(-500));
  changed('triage');
}

function mutes(): Mutes {
  syncStore();
  if (mutesCache) return mutesCache;
  const m = readJson<Partial<Mutes>>(MUTES, {});
  mutesCache = { places: m.places ?? [], channels: m.channels ?? [] };
  return mutesCache;
}

/** A place mute is keyed on normalizePlace + country, so "Al-Jawf" and "Al Jawf"
 *  mute together — the same normalization the grouping uses. */
export function placeMuteKey(e: EntityDto): string {
  const name = normalizePlace(e.attrs.place);
  const country = String(e.attrs.country ?? '').trim().toLowerCase();
  return name ? `${name}|${country}` : '';
}

export function channelsOf(e: EntityDto): string[] {
  const raw = e.attrs.sources;
  const list = Array.isArray(raw) && raw.length
    ? (raw as Record<string, unknown>[]).map((s) => String(s?.channel ?? ''))
    : [String(e.attrs.channel ?? '')];
  return list.map((c) => c.replace(/^@/, '').toLowerCase()).filter(Boolean);
}

export function isMuted(e: EntityDto): boolean {
  const m = mutes();
  const key = placeMuteKey(e);
  if (key && m.places.includes(key)) return true;
  if (!m.channels.length) return false;
  // Muted only when EVERY source is muted. One muted channel among three must not
  // hide a corroborated event — that would turn a noise filter into a blind spot.
  const chans = channelsOf(e);
  return chans.length > 0 && chans.every((c) => m.channels.includes(c));
}

export function mutePlace(e: EntityDto): void {
  const key = placeMuteKey(e);
  if (!key) return;
  const m = mutes();
  if (!m.places.includes(key)) m.places.push(key);
  writeJson(MUTES, m);
  changed('triage');
}

export function muteChannel(channel: string): void {
  const c = channel.replace(/^@/, '').toLowerCase();
  if (!c) return;
  const m = mutes();
  if (!m.channels.includes(c)) m.channels.push(c);
  writeJson(MUTES, m);
  changed('triage');
}

export function clearMutes(): void { writeJson(MUTES, { places: [], channels: [] }); changed('triage'); }

/** How much is being hidden, so it can always be SHOWN. A silent mute is a lie
 *  about coverage — the same failure as the damage window that once hid 422 of
 *  441 markers and read as "the toggles are broken". */
export function muteCount(): number {
  const m = mutes();
  return m.places.length + m.channels.length;
}

export function dismissedCount(): number { return ids(DISMISSED).size; }
